import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { formatRupiah, formatTanggalPendek } from "@/lib/format";
import { TrendingUp, Wallet, Receipt, Package, AlertTriangle, Trophy } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Metric = ({ icon: Icon, label, value, color, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6" data-testid={testid}>
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <p className="text-sm text-slate-500 mt-4">{label}</p>
    <p className="font-heading font-bold text-2xl text-slate-900 mt-1">{value}</p>
  </div>
);

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStr = () => new Date().toISOString().slice(0, 7);

export default function Laporan() {
  const [mode, setMode] = useState("semua"); // semua | harian | bulanan
  const [tanggal, setTanggal] = useState(todayStr());
  const [bulan, setBulan] = useState(monthStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    let params = {};
    if (mode === "harian") {
      params = { start: tanggal, end: tanggal };
    } else if (mode === "bulanan") {
      const [y, m] = bulan.split("-").map(Number);
      const lastDay = new Date(y, m, 0).getDate();
      params = { start: `${bulan}-01`, end: `${bulan}-${String(lastDay).padStart(2, "0")}` };
    }
    const res = await axios.get(`${API}/reports/summary`, { params });
    setData(res.data);
    setLoading(false);
  }, [mode, tanggal, bulan]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = (data?.daily || []).map((d) => ({
    name: formatTanggalPendek(d.tanggal),
    Omzet: Math.round(d.omzet),
    Laba: Math.round(d.laba),
  }));

  const modeBtn = (key, label) => (
    <button
      key={key}
      data-testid={`periode-${key}`}
      onClick={() => setMode(key)}
      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all active:scale-95 ${
        mode === key ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="px-8 pt-8 pb-4">
        <h2 className="font-heading font-bold text-2xl text-slate-900">Laporan Laba Rugi</h2>
        <p className="text-sm text-slate-500 mt-1">Ringkasan keuangan warung Anda</p>

        <div className="mt-5 flex items-center gap-3 flex-wrap">
          {modeBtn("semua", "Semua")}
          {modeBtn("harian", "Harian")}
          {modeBtn("bulanan", "Bulanan")}

          {mode === "harian" && (
            <input
              data-testid="periode-input-tanggal"
              type="date"
              value={tanggal}
              onChange={(e) => setTanggal(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none text-slate-900"
            />
          )}
          {mode === "bulanan" && (
            <input
              data-testid="periode-input-bulan"
              type="month"
              value={bulan}
              onChange={(e) => setBulan(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none text-slate-900"
            />
          )}
        </div>
      </div>

      {!data ? (
        <div className="p-8 text-slate-400">Memuat laporan...</div>
      ) : (
        <div className={`px-8 pb-8 space-y-6 transition-opacity ${loading ? "opacity-50" : "opacity-100"}`}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <Metric icon={Receipt} label="Total Omzet" value={formatRupiah(data.total_omzet)} color="bg-blue-50 text-blue-600" testid="metric-omzet" />
            <Metric icon={Wallet} label="Total Modal" value={formatRupiah(data.total_modal)} color="bg-amber-50 text-amber-600" testid="metric-modal" />
            <Metric icon={TrendingUp} label="Total Laba" value={formatRupiah(data.total_laba)} color="bg-emerald-50 text-emerald-600" testid="metric-laba" />
            <Metric icon={Package} label="Jumlah Transaksi" value={data.jumlah_transaksi} color="bg-violet-50 text-violet-600" testid="metric-transaksi" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
              <h3 className="font-heading font-bold text-slate-900">
                Tren Omzet &amp; Laba {mode === "bulanan" ? "(per hari)" : ""}
              </h3>
              <div className="mt-6 h-72" style={{ minHeight: 288 }} data-testid="chart-tren">
                {chartData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-400">Belum ada data transaksi pada periode ini</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} barGap={6}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => (v >= 1000 ? `${v / 1000}rb` : v)}
                      />
                      <Tooltip formatter={(v) => formatRupiah(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                      <Bar dataKey="Omzet" fill="#2563eb" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="Laba" fill="#10b981" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
              <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" /> Barang Terlaris
              </h3>
              <div className="mt-4 space-y-3" data-testid="list-terlaris">
                {(data.terlaris || []).length === 0 && (
                  <p className="text-sm text-slate-400 py-6 text-center">Belum ada penjualan</p>
                )}
                {(data.terlaris || []).map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-600 font-bold text-sm flex items-center justify-center">{i + 1}</span>
                    <span className="flex-1 text-sm font-medium text-slate-800 truncate">{t.nama}</span>
                    <span className="text-sm font-bold text-blue-600">{t.terjual}x</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
            <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" /> Stok Menipis
            </h3>
            <div className="mt-4" data-testid="list-stok-kritis">
              {(data.stok_kritis || []).length === 0 ? (
                <p className="text-sm text-slate-400 py-4">Semua stok aman 👍</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.stok_kritis.map((s, i) => (
                    <div key={i} className="flex items-center justify-between bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                      <span className="text-sm font-medium text-slate-800">{s.nama}</span>
                      <span className="text-sm font-bold text-red-600">Sisa {s.stok}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
